<script lang="ts">
import ArrowRight from "lucide-svelte/icons/arrow-right";
import Building2 from "lucide-svelte/icons/building-2";
import LockKeyhole from "lucide-svelte/icons/lock-keyhole";
import SectionHeading from "./section-heading.svelte";

const trustCenter = "https://trust.pre6.ai/";
</script>

<section class="security" aria-labelledby="security-title">
	<div class="sheet" data-drafting="security">
		<div class="head">
			<SectionHeading id="security-title" title="Security First Design" />
		</div>

		<div class="scale" aria-hidden="true"></div>

		<ul class="zones">
			<li class="zone">
				<span class="icon"><img src="/home/soc2-badge.webp" alt="" width="34" height="34" loading="lazy" decoding="async" /></span>
				<h3>SOC 2 compliance</h3>
				<p>SOC 2 Type II certified. Independently audited, continuously monitored.</p>
				<a class="trust" href={trustCenter} target="_blank" rel="noopener noreferrer">
					Trust Center
					<ArrowRight size={16} strokeWidth={2} class="trust-arrow" aria-hidden="true" />
					<span class="sr-only">(opens in a new tab)</span>
				</a>
			</li>
			<li class="zone">
				<span class="icon"><LockKeyhole size={20} strokeWidth={1.25} /></span>
				<h3>Airtight security</h3>
				<p>Encrypted at rest and in transit, kept apart from other customers, never used to train models.</p>
			</li>
			<li class="zone">
				<span class="icon"><Building2 size={20} strokeWidth={1.25} /></span>
				<h3>Enterprise readiness</h3>
				<p>Access control, incident response and data retention, written down as policy.</p>
			</li>
		</ul>
	</div>
</section>

<style>
	.security {
		--gutter: 44px;
		--tick: color-mix(in oklch, var(--muted-foreground) 55%, transparent);
		--tick-faint: color-mix(in oklch, var(--muted-foreground) 30%, transparent);
		width: 100%;
		max-width: var(--page-max);
		margin: 0 auto;
		padding: 0 32px 120px;
		color: var(--foreground);
	}

	.sheet {
		overflow: hidden;
		border: 1px solid var(--border);
		border-radius: 8px;
		background: var(--surface);
	}

	.head {
		padding: 44px var(--gutter) 36px;
	}

	/* A ruler above the squares, starting where the copy starts. */
	.scale {
		height: 18px;
		border-top: 1px solid var(--border);
		background-image:
			linear-gradient(90deg, var(--tick) 1px, transparent 1px),
			linear-gradient(90deg, var(--tick) 1px, transparent 1px),
			linear-gradient(90deg, var(--tick-faint) 1px, transparent 1px);
		background-size:
			80px 13px,
			40px 8px,
			8px 5px;
		background-position:
			var(--gutter) 100%,
			var(--gutter) 100%,
			var(--gutter) 100%;
		background-repeat: repeat-x;
	}

	/* Rows are shared across the squares, so their titles and text line up: mark, space, title, text, link. */
	.zones {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		grid-template-rows: auto 22px auto auto auto;
		border-top: 1px solid var(--border);
	}

	.zone {
		position: relative;
		display: grid;
		grid-row: span 5;
		grid-template-rows: subgrid;
		align-content: start;
		padding: 28px 32px 32px;
	}

	.zone > :first-child {
		grid-row: 1;
	}

	.zone h3 {
		grid-row: 3;
		margin-bottom: 8px;
	}

	.zone p {
		grid-row: 4;
	}

	.zone .trust {
		grid-row: 5;
		justify-self: start;
	}

	.zone:first-child {
		padding-left: var(--gutter);
	}

	.zone + .zone {
		border-left: 1px solid var(--border);
	}

	/* A registration cross where each divider meets the ruler. */
	.zone + .zone::before {
		content: "";
		position: absolute;
		top: -6px;
		left: -6px;
		width: 11px;
		height: 11px;
		background:
			linear-gradient(var(--tick), var(--tick)) center / 1px 100% no-repeat,
			linear-gradient(var(--tick), var(--tick)) center / 100% 1px no-repeat;
	}

	.icon img {
		width: 34px;
		height: 34px;
	}

	.icon {
		display: grid;
		place-items: center;
		width: 48px;
		height: 48px;
		border: 1px solid var(--border);
		background: #fff;
		color: var(--ink-soft);
	}

	h3 {
		color: var(--ink);
		font-family: "Inter Variable", var(--font-sans);
		font-variation-settings: "opsz" 32;
		font-size: 20px;
		line-height: 1.25;
		font-weight: 600;
		letter-spacing: -0.015em;
	}

	p {
		max-width: 28rem;
		color: var(--muted-foreground);
		font-size: 15.5px;
		line-height: 24px;
		text-wrap: pretty;
	}

	/* An underlined link with an arrow; on hover the arrow slides on and a glowing red line sweeps over the underline. */
	.trust {
		position: relative;
		display: inline-flex;
		align-items: center;
		gap: 6px;
		margin-top: 14px;
		padding-bottom: 5px;
		color: var(--ink);
		font-size: 15px;
		font-weight: 600;
	}

	.trust::before,
	.trust::after {
		content: "";
		position: absolute;
		right: 0;
		bottom: 0;
		left: 0;
		height: 1px;
	}

	.trust::before {
		background: currentColor;
	}

	.trust::after {
		height: 1.5px;
		background: var(--primary);
		box-shadow:
			0 0 6px color-mix(in oklch, var(--primary) 75%, transparent),
			0 0 14px color-mix(in oklch, var(--primary) 40%, transparent);
		transform: scaleX(0);
		transform-origin: left;
		transition: transform 0.2s ease-out;
	}

	.trust:hover::after {
		transform: scaleX(1);
	}

	.trust :global(.trust-arrow) {
		transition: translate 0.2s ease-out;
	}

	.trust:hover :global(.trust-arrow) {
		translate: 4px 0;
	}

	.trust:focus-visible {
		outline: 2px solid var(--ring);
		outline-offset: 4px;
		border-radius: 2px;
	}

	@media (max-width: 1023px) {
		.zones {
			display: flex;
			min-height: 0;
			flex-direction: column;
		}

		.zone,
		.zone:first-child {
			display: flex;
			flex-direction: column;
			align-items: flex-start;
			padding: 26px var(--gutter) 30px;
		}

		.zone > :first-child {
			margin-bottom: 22px;
		}

		.zone + .zone {
			border-top: 1px solid var(--border);
			border-left: 0;
		}

		.zone + .zone::before {
			display: none;
		}
	}

	@media (max-width: 767px) {
		.security {
			--gutter: 20px;
			padding: 0 16px 72px;
		}

		.head {
			padding-block: 32px 28px;
		}
	}
</style>
